import { useEffect, useState } from 'react'
import { AnalyticsConsentCard } from '../analytics/AnalyticsConsent'
import { HomepageV3Footer, HomepageV3Header } from '../components/HomepageV3Chrome'
import { cartBoxCount } from '../order/cartStorage'
import type { PublicStoreStatus } from './publicStoreStatus'
import '../styles/closed-store-state.css'

type ClosedStoreVariant = 'homepage' | 'order'
type ClosedStoreMode = 'closed' | 'checking' | 'unavailable'

function Arrow() {
  return <span className="sg-v3-arrow" aria-hidden="true">→</span>
}

export default function ClosedStoreState({
  variant,
  mode,
  status = null,
  showOrderProductCopy = false,
}: {
  variant: ClosedStoreVariant
  mode: ClosedStoreMode
  status?: PublicStoreStatus | null
  /** Explicit /order route opt-in; cart and checkout also use the order variant. */
  showOrderProductCopy?: boolean
}) {
  const [cartCount, setCartCount] = useState(() => cartBoxCount())

  useEffect(() => {
    const refreshCart = () => setCartCount(cartBoxCount())
    window.addEventListener('storage', refreshCart)
    const timer = window.setInterval(refreshCart, 500)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('storage', refreshCart)
    }
  }, [])

  const checking = mode === 'checking'
  const closed = mode === 'closed'
  const secondaryNote = variant === 'homepage'
    ? 'The Homepage stays accessible while ordering is paused.'
    : 'Ordering is unavailable until the store reopens.'

  return (
    <div className="sg-v3 closed-store-page">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} activeHref={variant === 'order' ? '/order' : undefined} cartEnabled={false} />
      <main id="main-content" tabIndex={-1}>
        {closed ? (
          <section className="closed-store-state" aria-labelledby="closed-store-title">
            <div className="sg-v3-shell closed-store-state__inner">
              <div className="closed-store-state__copy">
                <p className="sg-v3-eyebrow">SUGAR GREMLIN</p>
                <h1 id="closed-store-title">
                  <span>THIS WEEK’S DROP</span>
                  <strong>IS CLOSED</strong>
                </h1>
                <div className="closed-store-state__standard-copy">
                  <p>{variant === 'order' && showOrderProductCopy
                    ? 'Sugar Gremlin makes stuffed cookies, brownie cookie sandwiches and fudgy New York-style brownies in Christchurch. Mix available weekly flavours into a 4-pack or 6-pack for weekend delivery within our Christchurch delivery area.'
                    : 'We’re out of mischief for now.'}</p>
                  <p>Orders are currently paused.</p>
                  <p>Please check back for the next drop.</p>
                </div>
                {status ? (
                  <aside className="closed-store-state__message" aria-label="Store update">
                    <span>STORE UPDATE</span>
                    <p>{status.wording}</p>
                    {status.subtext ? <small>{status.subtext}</small> : null}
                  </aside>
                ) : null}
                <div className="closed-store-state__actions">
                  <a className="sg-v3-button sg-v3-button--pink" href="/faq">VIEW FAQS<Arrow /></a>
                  <a className="sg-v3-button sg-v3-button--outline" href="/contact">CONTACT US<Arrow /></a>
                </div>
                <p className="closed-store-state__note">{secondaryNote}</p>
              </div>
              <div className="closed-store-state__art">
                <img
                  src="/assets/closed-state/out-of-mischief-closed.png"
                  alt="Sleeping Sugar Gremlin mascot with bakery boxes, cookies and an Out of Mischief sign"
                />
              </div>
            </div>
          </section>
        ) : (
          <section
            className="closed-store-status-fallback"
            aria-labelledby="closed-store-status-title"
            role="status"
            aria-live="polite"
            aria-busy={checking}
          >
            <div className="sg-v3-shell closed-store-status-fallback__inner">
              <p className="sg-v3-eyebrow">{checking ? 'CHECKING STORE STATUS' : 'STORE STATUS UNAVAILABLE'}</p>
              <h1 id="closed-store-status-title">
                {checking ? 'Checking this week’s drop…' : 'Ordering is temporarily unavailable.'}
              </h1>
              <p>{checking ? 'One moment while we check whether ordering is open.' : 'Please refresh in a moment.'}</p>
            </div>
          </section>
        )}
      </main>
      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}
